/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListRootMcpServersRequest,
  ListRootMcpServersSecurity,
} from "../models/operations/listrootmcpservers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildRootMcpServersQuery,
  prefetchRootMcpServers,
  queryKeyRootMcpServers,
  RootMcpServersQueryData,
} from "./rootMcpServers.core.js";
export {
  buildRootMcpServersQuery,
  prefetchRootMcpServers,
  queryKeyRootMcpServers,
  type RootMcpServersQueryData,
};

export type RootMcpServersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRootMcpServers domains
 *
 * @remarks
 * List the organization's MCP servers that can be mapped to the custom domain root, including servers not yet attached to the domain
 */
export function useRootMcpServers(
  request?: ListRootMcpServersRequest | undefined,
  security?: ListRootMcpServersSecurity | undefined,
  options?: QueryHookOptions<RootMcpServersQueryData, RootMcpServersQueryError>,
): UseQueryResult<RootMcpServersQueryData, RootMcpServersQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildRootMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRootMcpServers domains
 *
 * @remarks
 * List the organization's MCP servers that can be mapped to the custom domain root, including servers not yet attached to the domain
 */
export function useRootMcpServersSuspense(
  request?: ListRootMcpServersRequest | undefined,
  security?: ListRootMcpServersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    RootMcpServersQueryData,
    RootMcpServersQueryError
  >,
): UseSuspenseQueryResult<RootMcpServersQueryData, RootMcpServersQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildRootMcpServersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setRootMcpServersData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: RootMcpServersQueryData,
): RootMcpServersQueryData | undefined {
  const key = queryKeyRootMcpServers(...queryKeyBase);

  return client.setQueryData<RootMcpServersQueryData>(key, data);
}

export function invalidateRootMcpServers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "domains",
      "listRootMcpServers",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllRootMcpServers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "domains", "listRootMcpServers"],
  });
}
